import { useEffect, useState, useMemo } from "react";
import { OfferCard } from "@/components/OfferCard";
import { OfferSection } from "@/components/OfferSection";
import { useOffers } from "@/hooks/useOffers";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { groupOffersBySections } from "@/utils/groupBySections";
import "./index.css";
import "@/types/telegram";

export function App() {
  const { offers, loading, error, refetch } = useOffers();
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Group offers by sections
  const groupedOffers = useMemo(() => {
    return groupOffersBySections(offers);
  }, [offers]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      tg.expand();
      
      // Detect if dark mode
      const isDark = tg.colorScheme === 'dark';
      setIsDarkMode(isDark);
      
      // Set theme variables with proper fallbacks
      document.documentElement.style.setProperty(
        '--tg-theme-bg-color',
        tg.themeParams.bg_color || (isDark ? '#1a1a1a' : '#ffffff')
      );
      document.documentElement.style.setProperty(
        '--tg-theme-text-color',
        tg.themeParams.text_color || (isDark ? '#ffffff' : '#000000')
      );
      document.documentElement.style.setProperty(
        '--tg-theme-button-color',
        tg.themeParams.button_color || '#3390ec'
      );
      
      // Add theme class to document
      if (isDark) {
        document.documentElement.classList.add('tg-dark');
      } else {
        document.documentElement.classList.remove('tg-dark');
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={isDarkMode ? 'text-white opacity-70' : 'text-gray-600'}>Загружаем предложения...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Ошибка загрузки</h2>
          <p className={isDarkMode ? 'text-white opacity-70' : 'text-gray-600'}>{error}</p>
          <Button onClick={refetch} variant="outline">
            Попробовать снова
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 max-w-4xl">
        <header className="text-center mb-8 pt-4">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Актуальные предложения
          </h1>
          <p className={isDarkMode ? 'text-white opacity-70' : 'text-gray-600'}>
            Для вас
          </p>
        </header>

        {offers.length === 0 ? (
          <div className="text-center py-12">
            <p className={`text-lg ${isDarkMode ? 'text-white opacity-70' : 'text-gray-600'}`}>
              Пока нет активных предложений
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedOffers.map((section) => (
              <OfferSection 
                key={section.sectionName} 
                section={section} 
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
